import React, { useEffect, useMemo,useState } from "react";
import { Dialog, DialogContent, DialogTitle, Box, Typography, TextField, Button, IconButton, Autocomplete } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import {
  createMandalAction,
  clearCreateMandalAction,
  getMandalByIdAction,
  updateMandalAction,
  clearUpdateMandalAction,
  getAllDistrictsAction,
  getAllUserAction,
  getAllMandalsAction,
} from "../../../store/actions";

const AddMandal = ({ open, onClose, isEdit, mandalId, onSuccess }) => {
  const dispatch = useDispatch();
 const [duplicateError, setDuplicateError] = useState("");

  // Reference lists
  const { successData: districtsData, loading: districtsLoading } = useSelector((state) => state.getAllDistrictsReducer || {});
  const { successData: usersData, loading: usersLoading } = useSelector((state) => state.getAllUserReducer || {});
  // Create/Update/ById reducers
  const { successData: createSuccess, loading: createLoading, error: createError, errorInfo: createErrorInfo } = useSelector((state) => state.createMandalReducer || {});
  const { successData: mandalByIdData, loading: mandalByIdLoading, error: mandalByIdError } = useSelector((state) => state.getMandalByIdReducer || {});
  const { successData: updateSuccess, loading: updateLoading, error: updateError, errorInfo: updateErrorInfo } = useSelector((state) => state.updateMandalReducer || {});

 

const normalizeMandalName = (name) => (name || "").replace(/\s+/g, "").toLowerCase();

const { successData: mandalsData, loading: mandalsLoading } = useSelector(
  (state) => state.getAllMandalsReducer || {}
);

const mandals = useMemo(() => {
  if (Array.isArray(mandalsData)) return mandalsData;
  if (mandalsData?.data && Array.isArray(mandalsData.data.mandals)) return mandalsData.data.mandals;
  return [];
}, [mandalsData]);

  const districts = useMemo(() => {
    if (Array.isArray(districtsData)) return districtsData;
    if (districtsData && districtsData.data && Array.isArray(districtsData.data.districts)) return districtsData.data.districts;
    return [];
  }, [districtsData]);

  const users = useMemo(() => {
    if (Array.isArray(usersData)) return usersData;
    if (usersData && usersData.data && Array.isArray(usersData.data.users)) return usersData.data.users;
    return [];
  }, [usersData]);

  const mdoUsers = useMemo(() => {
    return users.filter((u) => (u.role || '').toString().toUpperCase() === 'MDO');
  }, [users]);

  const mandalSchema = Yup.object({
    name: Yup.string().trim().required("Mandal name is required"),
    districtId: Yup.string().trim().required("District ID is required"),
  });

  const formik = useFormik({
    initialValues: { name: "", districtId: "", inchargeUserId: "" },
    validationSchema: mandalSchema,
    enableReinitialize: false,
    onSubmit: (values) => {
      const newNameNorm = normalizeMandalName(values.name);
      const duplicate = mandals.some((m) => {
        const mName = m?.name || m?.mandal_name || m?.mandalName || "";
        const mDistrictId = m?.districtId || m?.district_id || m?.districtID;
        const mId = m?.mandalId || m?.id || m?.mandal_id;
        if (isEdit && String(mId) === String(mandalId)) return false;
        return String(mDistrictId) === String(values.districtId) && normalizeMandalName(mName) === newNameNorm;
      });
      if (duplicate) {
        setDuplicateError("Mandal with this name already exists in this district!");
        return;
      }
      setDuplicateError("");
      const payload = {
        name: values.name,
        districtId: Number(values.districtId),
        ...(values.inchargeUserId ? { incharge_user_id: values.inchargeUserId } : {}),
      };
      if (isEdit && mandalId) {
        dispatch(updateMandalAction({ ...payload, endPoint: `geography/mandal/${mandalId}` }));
      } else {
        dispatch(createMandalAction(payload));
      }
    },
  });

  useEffect(() => {
    if (!formik.values.name || !formik.values.districtId) {
      setDuplicateError("");
      return;
    }
    const newNameNorm = normalizeMandalName(formik.values.name);
    const duplicate = mandals.some((m) => {
      const mName = m?.name || m?.mandal_name || m?.mandalName || "";
      const mDistrictId = m?.districtId || m?.district_id || m?.districtID;
      const mId = m?.mandalId || m?.id || m?.mandal_id;
      if (isEdit && String(mId) === String(mandalId)) return false;
      return String(mDistrictId) === String(formik.values.districtId) && normalizeMandalName(mName) === newNameNorm;
    });
    setDuplicateError(duplicate ? "Mandal with this name already exists in this district!" : "");
  }, [formik.values.name, formik.values.districtId, mandals, isEdit, mandalId]);
  // Fetch districts and users when dialog opens
  useEffect(() => {
    if (open) {
      dispatch(getAllDistrictsAction());
      dispatch(getAllUserAction());
      dispatch(getAllMandalsAction());
    }
  }, [open, dispatch]);

  // Fetch mandal by id when editing and dialog opens
  useEffect(() => {
    if (open && isEdit && mandalId) {
      dispatch(getMandalByIdAction({ endPoint: `geography/mandal/${mandalId}` }));
    }
  }, [open, isEdit, mandalId, dispatch]);

  // Prefill when mandal-by-id fetched
  useEffect(() => {
    if (open && isEdit && mandalByIdData && !mandalByIdLoading && !mandalByIdError) {
      const m = mandalByIdData?.data?.mandal || mandalByIdData?.data || mandalByIdData;
      const prefill = {
        name: m?.name || m?.mandal_name || m?.mandalName || "",
        districtId: String(m?.districtId ?? m?.districtID ?? m?.district_id ?? ""),
        inchargeUserId: String(m?.inchargeUserId ?? m?.incharge_user_id ?? m?.inchargeUser?.userId ?? ""),
      };
      formik.setValues(prefill);
    }
  }, [open, isEdit, mandalByIdData, mandalByIdLoading, mandalByIdError]);

  // Handle create success/error
  useEffect(() => {
    if (!open) return;
    if (createSuccess) {
      toast.success("Mandal created successfully!");
      formik.resetForm();
      dispatch(clearCreateMandalAction());
      onSuccess?.();
      onClose();
    }
    if (createError) {
      toast.error(createErrorInfo || "Failed to create mandal");
      dispatch(clearCreateMandalAction());
    }
  }, [open, createSuccess, createError, createErrorInfo, dispatch, formik, onClose, onSuccess]);

  // Handle update success/error
  useEffect(() => {
    if (!open) return;
    if (updateSuccess) {
      toast.success("Mandal updated successfully!");
      formik.resetForm();
      dispatch(clearUpdateMandalAction());
      onSuccess?.();
      onClose();
    }
    if (updateError) {
      toast.error(updateErrorInfo || "Failed to update mandal");
      dispatch(clearUpdateMandalAction());
    }
  }, [open, updateSuccess, updateError, updateErrorInfo, dispatch, formik, onClose, onSuccess]);

  const loading = createLoading || updateLoading || mandalByIdLoading;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: 4, p: { xs: 2, sm: 4 } } } }}>
      <IconButton onClick={onClose} sx={{ position: "absolute", top: 16, right: 16 }}>
        <CloseIcon />
      </IconButton>

      <DialogTitle sx={{ p: 0, mb: 1 }}>
        <Typography fontSize={28} fontWeight={700}>Mandal Information</Typography>
        <Typography fontSize={14} color="text.secondary">{isEdit ? "Update Mandal" : "Create new Mandal"}</Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 0, mt: 3 }}>
        <form onSubmit={formik.handleSubmit}>
          <Box mb={2}>
            <Typography fontSize={14} fontWeight={600} mb={1}>District</Typography>
            <Autocomplete
              options={districts.map((d) => ({
                id: d.districtId || d.id || d.district_id || d.districtID,
                name: d.district_name || d.districtName || d.name || String(d.districtId || d.id || d.district_id || d.districtID || ""),
              })).filter((o) => o.id)}
              value={(function () {
                const currentId = formik.values.districtId;
                if (!currentId) return null;
                const found = districts.find((d) => {
                  const did = d.districtId || d.id || d.district_id || d.districtID;
                  return String(did) === String(currentId);
                });
                return found
                  ? {
                      id: found.districtId || found.id || found.district_id || found.districtID,
                      name: found.district_name || found.districtName || found.name || String(found.districtId || found.id || found.district_id || found.districtID || ""),
                    }
                  : null;
              })()}
              onChange={(_, newValue) => {
                formik.setFieldValue("districtId", newValue ? String(newValue.id) : "");
              }}
              loading={districtsLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={districtsLoading ? "Loading districts..." : "Select a district"}
                  fullWidth
                  size="small"
                  error={formik.touched.districtId && Boolean(formik.errors.districtId)}
                  helperText={formik.touched.districtId && formik.errors.districtId}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                />
              )}
              getOptionLabel={(option) => option?.name ?? ""}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
            />
          </Box>

          <Box mb={4}>
            <Typography fontSize={14} fontWeight={600} mb={1}>Mandal Name</Typography>
            <TextField
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              size="small"
              placeholder="Enter Mandal"
              error={!!duplicateError || (formik.touched.name && Boolean(formik.errors.name))}
              helperText={duplicateError ? duplicateError : (formik.touched.name && formik.errors.name)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
            />
          </Box>

             <Box mb={2}>
            <Typography fontSize={14} fontWeight={600} mb={1}>Incharge User</Typography>
            <Autocomplete
              options={mdoUsers.map((u) => ({
                id: u.userId || u.id,
                name: u.name || "",
                role: u.role || "",
              })).filter((o) => o.id)}
              value={(function () {
                const currentId = formik.values.inchargeUserId;
                if (!currentId) return null;
                const found = mdoUsers.find((u) => {
                  const uid = u.userId || u.id;
                  return String(uid) === String(currentId);
                });
                return found
                  ? { id: found.userId || found.id, name: found.name || "", role: found.role || "" }
                  : null;
              })()}
              onChange={(_, newValue) => {
                formik.setFieldValue("inchargeUserId", newValue ? String(newValue.id) : "");
              }}
              loading={usersLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={usersLoading ? "Loading  users..." : "Select an incharge "}
                  fullWidth
                  size="small"
                  error={formik.touched.inchargeUserId && Boolean(formik.errors.inchargeUserId)}
                  helperText={formik.touched.inchargeUserId && formik.errors.inchargeUserId}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                />
              )}
              getOptionLabel={(option) => option?.name ? `${option.name}${option.role ? ` (${option.role})` : ''}` : ""}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
            />
          </Box>

          <Box display="flex" gap={2} flexDirection="row">
            <Button
              fullWidth
              variant="outlined"
              onClick={onClose}
              disabled={loading}
              sx={{ borderRadius: 3, fontWeight: 600, p: "10px 0px", color: "#000", border: "1px solid #000", textTransform: "none" }}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || !!duplicateError}
              sx={{ borderRadius: 3, p: "10px 0px", fontWeight: 600, textTransform: "none", bgcolor: "#5B4CF5", boxShadow: "0px 8px 20px rgba(91,76,245,0.3)", "&:hover": { bgcolor: "#4a3de0" } }}
            >
              {loading ? (isEdit ? "Updating..." : "Submitting...") : isEdit ? "Update Mandal" : "Submit Data"}
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

AddMandal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  isEdit: PropTypes.bool,
  mandalId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSuccess: PropTypes.func,
};

AddMandal.defaultProps = {
  isEdit: false,
  mandalId: "",
  onSuccess: undefined,
};

export default AddMandal;